import {
    Button,
    Card,
    CardContent,
    CardHeader,
    colors,
    Grid,
    makeStyles,
    Switch,
    TextField,
    Theme, Typography
} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import useRouter from 'use-react-router';
import validate from 'validate.js';
import {Can} from '../../../components/Can';
import ConfirmDelete from '../../../components/ConfirmDelete';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import anyLoading from '../../../utils/anyLoading';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import {CmsCopy} from '../../../graphql/common.datatypes';
import createCmsCopy, {CreateCmsCopyInput} from './createCmsCopy.api';
import updateCmsCopy, {UpdateCmsCopyInput} from './updateCmsCopy.api';
import deleteCmsCopy from './deleteCmsCopy.api';
import MediumEditorField from '../../../components/MediumEditor/MediumEditorField';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        '&:hover': {
            backgroundColor: theme.palette.error.main
        }
    },
    cloneButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[400],
        '&:hover': {
            backgroundColor: colors.green[700]
        }
    },
    errorIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.error.main,
    },
    marginBottom: {
        paddingLeft: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    tabs: {
        marginTop: theme.spacing(3)
    },
    textField: {
        width: '100%',
    },
}));


export const CmsCopyEdit = (props: { cmsCopy: CmsCopy }) => {
    const classes = useStyles();
    const {cmsCopy} = props;
    const isNewCopy = cmsCopy.id === -1;
    const {history} = useRouter();
    const [open, setOpen] = React.useState(false);
    const {enqueueSnackbar} = useSnackbar();
    const [sendCreateCmsCopy, createData] = createCmsCopy();
    const [sendUpdateCmsCopy, updateData] = updateCmsCopy();
    const [sendDeleteCmsCopy, deleteData] = deleteCmsCopy();

    //1. Set form and error state
    const [formState, setFormState] = useState({...cmsCopy,});
    const loadingData = anyLoading(createData, updateData, deleteData);
    const [errors, setErrors] = useState({
        isValid: false,
        errors: {
            key: false,
            body: false,
        }
    });

    useEffect(() => {
        setFormState({...props.cmsCopy});
    }, [props.cmsCopy])

    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        key: {
            presence: {allowEmpty: false, message: 'is required'},
        },
        body: {
            presence: {allowEmpty: false, message: 'is required'},
        },
    };

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);
        const isValid = !newErrors;

        setErrors(state => ({
            ...state,
            isValid,
            errors: newErrors || {}
        }));
    }, [formState]);

    //4. Handle save form
    const onSaveClick = () => {
        if (isNewCopy) {
            const sanitisedData: CreateCmsCopyInput = {
                data: {
                    ...{
                        ...formState,
                        id: undefined,
                        __typename: undefined,
                    },
                }
            };

            sendCreateCmsCopy({variables: sanitisedData})
                .then((response) => {
                    enqueueSnackbar('Cms Copy Saved.', {variant: 'success'});
                    history.push('/cms_copy/');
                })
                .catch(() => {
                    enqueueSnackbar('Save Cms Copy Failed!', {variant: 'error'});
                });
        } else {
            const sanitisedData: UpdateCmsCopyInput = {
                data: {
                    ...{
                        ...formState,
                        __typename: undefined,
                    },
                }
            };

            sendUpdateCmsCopy({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Cms Copy Updated.', {variant: 'success'});
                })
                .catch(() => {
                    enqueueSnackbar('Cms Copy Update Failed!', {variant: 'error'});
                });
        }
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDelete = () => {
        sendDeleteCmsCopy({variables: {id: formState.id}})
            .then(() => {
                enqueueSnackbar('Cms Copy Deleted.', {variant: 'success'});
                history.push('/cms_copy');
            })
            .catch(() => {
                enqueueSnackbar('Deleted Cms Copy Failed!', {variant: 'error'});
            });

        handleClose();
    };

    return (
        <form>
            <LoadingOverlay loading={loadingData.loading}>

                <Card className={classes.container}>
                    <CardHeader title="Edit Cms Copy"/>
                    <CardContent className={classes.card}>

                        <Grid container spacing={3}>

                            <Grid item xs={12} style={{display: 'flex'}}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Key"
                                            name="key"
                                            onChange={handleFieldChange}
                                            {...hasError('key')}
                                            value={formState.key}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        {formState.is_html && (
                                            <MediumEditorField
                                                {...hasError('body')}
                                                fullWidth
                                                label="Body"
                                                variant="outlined"
                                                name="body"
                                                multiline
                                                value={formState.body}
                                                onChange={(event: any) => {
                                                    console.log('hello');
                                                    setFormState({
                                                        ...formState,
                                                        body: event.target.value
                                                    })
                                                }}
                                            />
                                        )}
                                        {!formState.is_html && (
                                            <TextField
                                                fullWidth
                                                label="Body"
                                                name="body"
                                                variant="outlined"
                                                multiline
                                                rows={3}
                                                rowsMax={8}
                                                onChange={handleFieldChange}
                                                {...hasError('body')}
                                                value={formState.body}
                                            />
                                        )}
                                    </Grid>

                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid component="label" container alignItems="center" spacing={1}>
                                    <Grid item>
                                        <Switch
                                            checked={formState.is_html}
                                            color="secondary"
                                            edge="start"
                                            name="is_html"
                                            onChange={handleFieldChange}
                                            value={formState.is_html}
                                        />
                                    </Grid>
                                    <Grid item><Typography variant="h5">Is Html?</Typography></Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Button className={classes.saveButton} onClick={onSaveClick} variant="contained"
                                        disabled={!errors.isValid}>Save</Button>
                                <Can permission="groups.delete">
                                    <Button onClick={handleClickOpen} className={classes.deleteButton}
                                            variant="contained"
                                            disabled={isNewCopy}>
                                        Delete
                                    </Button>
                                </Can>
                            </Grid>

                        </Grid>
                    </CardContent>
                </Card>

            </LoadingOverlay>

            <ConfirmDelete onClose={handleClose} onDeleteClick={handleDelete} open={open}/>
        </form>
    );
};

export default CmsCopyEdit;